import { useCallback, useMemo } from 'react';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useGetWhatsappTemplatesQuery } from '@/services/endpoints/campaigns';
import { WhatsappTemplateCategoryApi, WhatsappTemplateResponseItem, WhatsappTemplateStatusApi } from '@/services/endpoints/campaigns/types';
import { WhatsappTemplate, WhatsappTemplateCategory, WhatsappTemplateInput, WhatsappTemplateStatus } from '../types/whatsappTemplates';
import { sanitizeTemplateHtml } from '../utils/templateHtml';

const USE_WHATSAPP_TEMPLATES_API = true;

const wait = (delayMs = 160) => new Promise((resolve) => setTimeout(resolve, delayMs));

const nowAsLabel = () => {
  const formatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date());

  return `Updated ${formatted}`;
};

const STATUS_MAP: Record<string, WhatsappTemplateStatus> = {
  APPROVED: 'approved',
  IN_REVIEW: 'inReview',
  REJECTED: 'rejected',
  DRAFT: 'draft',
  PENDING: 'pending',
};

const CATEGORY_MAP: Record<string, WhatsappTemplateCategory> = {
  MARKETING: 'marketing',
  UTILITY: 'utility',
  AUTHENTICATION: 'authentication',
};

const mapStatus = (status?: WhatsappTemplateStatusApi): WhatsappTemplateStatus => {
  if (!status) {
    return 'draft';
  }

  return STATUS_MAP[status] ?? 'draft';
};

const mapCategory = (category?: WhatsappTemplateCategoryApi): WhatsappTemplateCategory => {
  if (!category) {
    return 'marketing';
  }

  return CATEGORY_MAP[category] ?? 'marketing';
};

const formatUpdatedLabel = (rawDate?: string) => {
  if (!rawDate) {
    return 'N/A';
  }

  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  return `Updated ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date)}`;
};

const mapTemplateResponseToUi = (item: WhatsappTemplateResponseItem): WhatsappTemplate => {
  return {
    id: item.id || `template_${Date.now()}`,
    name: item.name || 'N/A',
    body: sanitizeTemplateHtml(item.body || 'N/A'),
    language: item.language || 'N/A',
    category: mapCategory(item.category),
    status: mapStatus(item.status),
    updatedAt: formatUpdatedLabel(item.updatedOn || item.lastModified),
  };
};

const createTemplate = async (input: WhatsappTemplateInput) => {
  await wait();
  const next: WhatsappTemplate = {
    id: `${input.name.trim().toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`,
    name: input.name.trim(),
    body: sanitizeTemplateHtml(input.body),
    language: 'Italian · v1',
    category: 'marketing',
    status: 'draft',
    updatedAt: nowAsLabel(),
  };

  return next;
};

const updateTemplate = async (templateId: string, input: WhatsappTemplateInput): Promise<WhatsappTemplate> => {
  await wait();
  return {
    id: templateId,
    name: input.name.trim() || 'N/A',
    body: sanitizeTemplateHtml(input.body),
    language: 'N/A',
    category: 'marketing',
    status: 'draft',
    updatedAt: nowAsLabel(),
  };
};

export type WhatsappWabaAccount = {
  wabaId: string;
  wabaName: string;
};

export const useWhatsappTemplates = () => {
  const workspace = useWorkspace();
  const { data, isFetching, refetch } = useGetWhatsappTemplatesQuery({ workspaceId: workspace.id }, { skip: !workspace.id });

  const wabaAccounts = useMemo<WhatsappWabaAccount[]>(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map(({ wabaId, wabaName }) => ({ wabaId, wabaName }));
  }, [data]);

  const templatesByWaba = useMemo(() => {
    if (!Array.isArray(data)) {
      return {} as Record<string, WhatsappTemplate[]>;
    }

    return data.reduce<Record<string, WhatsappTemplate[]>>((acc, item) => {
      acc[item.wabaId] = item.templates.map(mapTemplateResponseToUi);
      return acc;
    }, {});
  }, [data]);

  const getTemplatesForWaba = useCallback(
    (wabaId: string) => templatesByWaba[wabaId] ?? [],
    [templatesByWaba]
  );

  const templates = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    const flattened = data.flatMap((item) => item.templates);
    return flattened.map(mapTemplateResponseToUi);
  }, [data]);

  const create = useCallback(
    async (input: WhatsappTemplateInput) => {
      const created = await createTemplate(input);
      return [created, ...templates];
    },
    [templates]
  );

  const update = useCallback(
    async (templateId: string, input: WhatsappTemplateInput) => {
      const updated = await updateTemplate(templateId, input);
      return templates.map((item) => (item.id === templateId ? updated : item));
    },
    [templates]
  );

  const getById = useCallback(async (templateId: string) => templates.find((item) => item.id === templateId), [templates]);

  return {
    templates,
    wabaAccounts,
    templatesByWaba,
    getTemplatesForWaba,
    templatesCount: templates.length,
    isLoading: isFetching,
    reload: refetch,
    create,
    update,
    getById,
    isUsingApi: USE_WHATSAPP_TEMPLATES_API,
    data,
  };
};
